import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { Backdrop, CircularProgress } from '@mui/material';
import { normalizeSkinUrl } from '../../../utils/skinUrl';

import { createSkin, updateSkin } from '../../../api/skins';

function splitTags(input) {
    return String(input || '')
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean);
}

export default function SkinDialog({ open, editing, onClose, onSaved }) {
    const isEdit = Boolean(editing?._id);

    const [title, setTitle] = useState('');
    const [tagsText, setTagsText] = useState('');
    const [isPremium, setIsPremium] = useState(false);
    const [saving, setSaving] = useState(false);

    const [cView, setCView] = useState(0);
    const [cLike, setCLike] = useState(0);
    const [cDownload, setCDownload] = useState(0);

    const [thumbFile, setThumbFile] = useState(null);
    const [skinImageFile, setSkinImageFile] = useState(null);

    const thumbPreview = useMemo(() => (thumbFile ? URL.createObjectURL(thumbFile) : ''), [thumbFile]);
    const skinPreview = useMemo(() => (skinImageFile ? URL.createObjectURL(skinImageFile) : ''), [skinImageFile]);

    useEffect(() => {
        if (!open) return;

        if (isEdit) {
            setTitle(editing.title || '');
            setTagsText((editing.tags || []).join(', '));
            setIsPremium(Boolean(editing.isPremium));
            setCView(Number(editing.c_view || 0));
            setCLike(Number(editing.c_like || 0));
            setCDownload(Number(editing.c_download || 0));
        } else {
            setTitle('');
            setTagsText('MCSkin');
            setIsPremium(false);
            setCView(0);
            setCLike(0);
            setCDownload(0);
        }

        setThumbFile(null);
        setSkinImageFile(null);
    }, [open, isEdit, editing]);

    useEffect(() => {
        return () => {
            if (thumbPreview) URL.revokeObjectURL(thumbPreview);
            if (skinPreview) URL.revokeObjectURL(skinPreview);
        };
    }, [thumbPreview, skinPreview]);

    const submit = async () => {
        if (!title.trim()) {
            alert('Title is required');
            return;
        }

        if (!isEdit) {
            if (!thumbFile || !skinImageFile) {
                alert('Thumb and Skin Image are required for new skin');
                return;
            }
        }

        try {
            setSaving(true);

            const fd = new FormData();
            fd.append('title', title.trim());
            fd.append('tags', JSON.stringify(splitTags(tagsText)));
            fd.append('isPremium', String(isPremium));
            fd.append('c_view', String(Number(cView) || 0));
            fd.append('c_like', String(Number(cLike) || 0));
            fd.append('c_download', String(Number(cDownload) || 0));

            if (thumbFile) fd.append('thumb', thumbFile);
            if (skinImageFile) fd.append('skinImage', skinImageFile);

            if (isEdit) {
                await updateSkin(editing._id, fd);
            } else {
                await createSkin(fd);
            }

            onSaved();
        } catch (err) {
            console.error(err);
            alert('Something went wrong');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{isEdit ? 'Edit Skin' : 'New Skin'}</DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        fullWidth
                        size="small"
                    />

                    <TextField
                        label="Tags (comma separated)"
                        value={tagsText}
                        onChange={(e) => setTagsText(e.target.value)}
                        fullWidth
                        size="small"
                        helperText="Default tag MCSkin will be ensured by API."
                    />

                    <FormControlLabel
                        control={<Switch checked={isPremium} onChange={(e) => setIsPremium(e.target.checked)} />}
                        label="Premium"
                    />

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <TextField
                            label="c_view"
                            type="number"
                            value={cView}
                            onChange={(e) => setCView(e.target.value)}
                            size="small"
                            sx={{ width: 160 }}
                        />
                        <TextField
                            label="c_like"
                            type="number"
                            value={cLike}
                            onChange={(e) => setCLike(e.target.value)}
                            size="small"
                            sx={{ width: 160 }}
                        />
                        <TextField
                            label="c_download"
                            type="number"
                            value={cDownload}
                            onChange={(e) => setCDownload(e.target.value)}
                            size="small"
                            sx={{ width: 160 }}
                        />
                    </Stack>

                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Thumb {isEdit ? '(optional replace)' : '(required)'}
                            </Typography>
                            <Button variant="outlined" component="label">
                                Choose thumb
                                <input
                                    hidden
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp"
                                    onChange={(e) => setThumbFile(e.target.files?.[0] || null)}
                                />
                            </Button>

                            <Box sx={{ mt: 1 }}>
                                <img
                                    src={thumbPreview || (isEdit ? normalizeSkinUrl(editing?.thumb) : '')}
                                    alt=""
                                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 12, border: '1px solid #eee' }}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                Skin Image {isEdit ? '(optional replace)' : '(required)'}
                            </Typography>
                            <Button variant="outlined" component="label">
                                Choose skin image
                                <input
                                    hidden
                                    type="file"
                                    accept=".png,.jpg,.jpeg,.webp"
                                    onChange={(e) => setSkinImageFile(e.target.files?.[0] || null)}
                                />
                            </Button>

                            <Box sx={{ mt: 1 }}>
                                <img
                                    src={skinPreview || (isEdit ? normalizeSkinUrl(editing?.skinImage) : '')}
                                    alt=""
                                    style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 12, border: '1px solid #eee' }}
                                />
                            </Box>
                        </Box>
                    </Stack>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={saving}>
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
            <Backdrop
                open={saving}
                sx={{
                    position: 'absolute',
                    zIndex: 10,
                    color: '#fff',
                }}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </Dialog>
    );
}